import { useState } from 'react'

import { useTranslation } from 'react-i18next'

import {
  BorderRadius,
  Flex,
  LightModeSemanticColors,
  SecondaryButton,
  Spacing,
  Text,
} from '@enechain/ecloud-designsystem'
import {
  FaChevronDownRegular,
  FaChevronRightRegular,
} from '@enechain/ecloud-icons'

import { TABLE_HEADER_CELL_STYLE } from '~/components/common/MasterDataTable'
import { iconSize } from '~/constants/iconSize'
import { typography } from '~/constants/typography'
import type { VoltageClass } from '~/models/voltageBreakdown'
import type { ContractTrendState } from '~/pages/CustomerInfo/hooks/useContractTrend'
import {
  type TrendDay,
  type TrendEvent,
  type TrendMonth,
  VOLTAGES,
  groupByFiscalYear,
} from '~/pages/CustomerInfo/models/contractTrend'
import { VOLTAGE_I18N_KEY, formatKw } from '~/pages/CustomerInfo/models/display'

type Props = {
  state: ContractTrendState
  /** 見出しに出すエリア名 (「すべて」を含む)。 */
  areaLabel: string
}

type SortKey = 'date' | 'count' | VoltageClass

const borderColor = LightModeSemanticColors.Border.lowEmphasis.value
const surface = LightModeSemanticColors.Surface.primary.value
const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土']

const cell: React.CSSProperties = {
  ...typography.tableCell,
  padding: '6px 10px',
  borderBottom: `1px solid ${borderColor}`,
  whiteSpace: 'nowrap',
}
const numCell: React.CSSProperties = { ...cell, textAlign: 'right' }

const toggleStyle: React.CSSProperties = {
  alignItems: 'center',
  background: 'none',
  border: 0,
  cursor: 'pointer',
  display: 'flex',
  gap: Spacing[3].value,
  paddingBlock: Spacing[2].value,
  paddingInline: Spacing[4].value,
  textAlign: 'left',
  width: '100%',
  ...typography.tableCell,
}

const pad = (n: number): string => String(n).padStart(2, '0')
const dayKey = (d: TrendDay['date']): string => `${d.year}-${d.month}-${d.day}`
const weekday = (d: TrendDay['date']): string =>
  WEEKDAYS[new Date(Date.UTC(d.year, d.month - 1, d.day)).getUTCDay()]
/**
 * 契約kW 一覧 (年度 > 月 > 日のアコーディオン。 AC-CIN-005: 展開すると日別を出す。 年度は最初は開き、 月は閉じている)。
 * 年度・月の行はラベルだけ。 月を開くと日ごとの 電圧階級別の契約kW・前日比・需要家数・変動 (開始 / 終了の
 * タグ) の表を出す (列の見出しは並べ替えできる)。 値は backend の日別集計 (GetContractTrend) で、 ここでは並べるだけ。
 */
export const ContractMonthlyTable = ({
  state,
  areaLabel,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { trend } = state
  const visible = VOLTAGES.filter((v) => state.voltages.has(v))
  const [opened, setOpened] = useState<string[]>([])
  // 年度は最初は開く (閉じたものだけ持つ)
  const [closedFy, setClosedFy] = useState<number[]>([])
  const fiscalYears = groupByFiscalYear(trend.months)
  const [sortKey, setSortKey] = useState<SortKey>('date')
  const [ascending, setAscending] = useState(true)
  const allOpen =
    trend.months.length > 0 &&
    closedFy.length === 0 &&
    opened.length === trend.months.length

  const sortBy = (key: SortKey): void => {
    if (sortKey === key) setAscending(!ascending)
    else {
      setSortKey(key)
      setAscending(key !== 'date')
    }
  }
  const eventsOf = (day: TrendDay): TrendEvent[] =>
    trend.events.filter(
      (e) =>
        e.date.year === day.date.year &&
        e.date.month === day.date.month &&
        e.date.day === day.date.day,
    )
  const valueOf = (day: TrendDay, key: SortKey): number =>
    key === 'count' ? day.count : key === 'date' ? 0 : day.kw[key]
  const sorted = (days: TrendDay[]): TrendDay[] => {
    if (sortKey === 'date') return ascending ? days : [...days].reverse()
    return [...days].sort((a, b) =>
      ascending
        ? valueOf(a, sortKey) - valueOf(b, sortKey)
        : valueOf(b, sortKey) - valueOf(a, sortKey),
    )
  }
  const previousOf = (day: TrendDay): TrendDay | undefined => {
    const i = trend.days.indexOf(day)
    return i > 0 ? trend.days[i - 1] : undefined
  }

  const header = (
    key: SortKey,
    label: string,
    numeric: boolean,
  ): React.ReactElement => (
    <th
      key={key}
      aria-sort={
        sortKey === key ? (ascending ? 'ascending' : 'descending') : undefined
      }
      onClick={() => sortBy(key)}
      style={{
        ...TABLE_HEADER_CELL_STYLE,
        cursor: 'pointer',
        textAlign: numeric ? 'right' : 'left',
      }}
    >
      {label}
    </th>
  )

  const renderMonth = (m: TrendMonth): React.ReactElement => {
    const isOpen = opened.includes(m.key)
    return (
      <div
        key={m.key}
        style={{
          background: surface,
          border: `1px solid ${borderColor}`,
          borderRadius: BorderRadius.radiiLg.value,
          marginBottom: Spacing[1].value,
          overflow: 'hidden',
        }}
      >
        <button
          aria-expanded={isOpen}
          onClick={() =>
            setOpened(
              isOpen ? opened.filter((k) => k !== m.key) : [...opened, m.key],
            )
          }
          style={{
            alignItems: 'center',
            background: 'none',
            border: 0,
            cursor: 'pointer',
            display: 'flex',
            gap: Spacing[3].value,
            paddingBlock: Spacing[2].value,
            paddingInline: Spacing[4].value,
            textAlign: 'left',
            width: '100%',
            ...typography.tableCell,
          }}
          type="button"
        >
          {isOpen ? (
            <FaChevronDownRegular boxSize={iconSize.labeledButton} />
          ) : (
            <FaChevronRightRegular boxSize={iconSize.labeledButton} />
          )}
          <span style={{ fontWeight: 600 }}>
            {t('page:customerInfo.trend.table.month', {
              year: m.year,
              month: m.month,
            })}
          </span>
        </button>
        {isOpen && (
          <div style={{ borderTop: `1px solid ${borderColor}` }}>
            <table
              aria-label={t('page:customerInfo.trend.table.ariaLabel')}
              style={{ borderCollapse: 'collapse', width: '100%' }}
            >
              <thead>
                <tr>
                  {header(
                    'date',
                    t('page:customerInfo.trend.table.date'),
                    false,
                  )}
                  {visible.flatMap((v) => [
                    header(v, `${t(VOLTAGE_I18N_KEY[v])}(kW)`, true),
                    <th
                      key={`${v}-delta`}
                      style={{
                        ...TABLE_HEADER_CELL_STYLE,
                        textAlign: 'right',
                      }}
                    >
                      {t('page:customerInfo.trend.table.delta')}
                    </th>,
                  ])}
                  {header(
                    'count',
                    t('page:customerInfo.trend.kpi.customers'),
                    true,
                  )}
                  <th style={TABLE_HEADER_CELL_STYLE}>
                    {t('page:customerInfo.trend.table.change')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sorted(m.days).map((day) => {
                  const prev = previousOf(day)
                  const events = eventsOf(day)
                  return (
                    <tr key={dayKey(day.date)}>
                      <td style={cell}>
                        {`${pad(day.date.month)}/${pad(day.date.day)}`}
                        <Text as="span" marginLeft={1} color="Text.midEmphasis">
                          {weekday(day.date)}
                        </Text>
                      </td>
                      {visible.flatMap((v) => {
                        const delta =
                          prev === undefined
                            ? undefined
                            : day.kw[v] - prev.kw[v]
                        return [
                          <td key={v} style={numCell}>
                            {formatKw(day.kw[v])}
                          </td>,
                          <td key={`${v}-d`} style={numCell}>
                            {delta === undefined
                              ? '—'
                              : `${delta > 0 ? '+' : ''}${formatKw(delta)}`}
                          </td>,
                        ]
                      })}
                      <td style={numCell}>{day.count.toLocaleString()}</td>
                      <td style={cell}>
                        {events.map((e, i) => (
                          <Text
                            key={`${e.kind}-${e.customerName}-${i}`}
                            as="span"
                            marginRight={2}
                            title={`${e.customerName} (${t(VOLTAGE_I18N_KEY[e.voltage])}) ${formatKw(e.kw)}kW`}
                          >
                            {`${e.kind === 'start' ? '▲' : '▼'}${e.customerName}`}
                          </Text>
                        ))}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    )
  }

  return (
    <Flex flexDirection="column" gap={2}>
      <Flex alignItems="center" justifyContent="space-between">
        <Text textStyle="sectionTitle">
          {t('page:customerInfo.trend.table.title', { area: areaLabel })}
        </Text>
        <Flex alignItems="center" gap={2}>
          <SecondaryButton
            onClick={() => {
              setOpened(allOpen ? [] : trend.months.map((m) => m.key))
              setClosedFy(allOpen ? fiscalYears.map((f) => f.fy) : [])
            }}
            size="sm"
          >
            {allOpen
              ? t('page:customerInfo.trend.table.closeAll')
              : t('page:customerInfo.trend.table.openAll')}
          </SecondaryButton>
        </Flex>
      </Flex>
      {trend.months.length === 0 ? (
        <Text color="Text.midEmphasis">
          {t('page:customerInfo.trend.empty')}
        </Text>
      ) : (
        <>
          {fiscalYears.map((f) => {
            const isFyOpen = !closedFy.includes(f.fy)
            return (
              <div
                key={f.fy}
                style={{
                  background: surface,
                  border: `1px solid ${borderColor}`,
                  borderRadius: BorderRadius.radiiLg.value,
                  marginBottom: Spacing[2].value,
                  overflow: 'hidden',
                }}
              >
                <button
                  aria-expanded={isFyOpen}
                  onClick={() =>
                    setClosedFy(
                      isFyOpen
                        ? [...closedFy, f.fy]
                        : closedFy.filter((k) => k !== f.fy),
                    )
                  }
                  style={{ ...toggleStyle, fontWeight: 600 }}
                  type="button"
                >
                  {isFyOpen ? (
                    <FaChevronDownRegular boxSize={iconSize.labeledButton} />
                  ) : (
                    <FaChevronRightRegular boxSize={iconSize.labeledButton} />
                  )}
                  <span style={{ fontWeight: 600 }}>
                    {t('page:customerInfo.trend.table.fiscalYear', {
                      fy: f.fy,
                      from: `${f.fy}/04`,
                      to: `${f.fy + 1}/03`,
                    })}
                  </span>
                </button>
                {isFyOpen && (
                  <div
                    style={{
                      borderTop: `1px solid ${borderColor}`,
                      paddingBlock: Spacing[2].value,
                      paddingInline: Spacing[4].value,
                    }}
                  >
                    {f.months.map((m) => renderMonth(m))}
                  </div>
                )}
              </div>
            )
          })}
        </>
      )}
    </Flex>
  )
}
